import {
  MarketingDateTriggerStep,
  TriggerStep,
} from "@redotech/redo-model/advanced-flow/advanced-flow-db-parser";
import {
  TimeframeConditionType,
  TimeframeUnit,
} from "@redotech/redo-model/marketing/segments/segment-timeframe";
import { CustomerCharacteristicType } from "@redotech/redo-model/marketing/segments/segment-types";
import { RedoIncrementDecrement } from "@redotech/redo-web/arbiter-components/increment-decrement/redo-increment-decrement";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { assertNever } from "@redotech/util/type";
import { memo, useCallback, useMemo } from "react";
import { PartialWithRequiredType } from "../../steps";

export function dateTriggerStepIsValid(
  value: PartialWithRequiredType<MarketingDateTriggerStep>,
): boolean {
  if (!value.triggerSpecificFields?.comparison?.type) {
    return false;
  }
  if (
    (value.triggerSpecificFields.comparison.type ===
      TimeframeConditionType.AFTER_NOW_RELATIVE_EXACT ||
      value.triggerSpecificFields.comparison.type ===
        TimeframeConditionType.BEFORE_NOW_RELATIVE_EXACT) &&
    value.triggerSpecificFields.comparison.options?.value === undefined
  ) {
    return false;
  }
  return true;
}

const dateTriggerDimensionOptions: RedoListItem<CustomerCharacteristicType>[] =
  [
    {
      value: CustomerCharacteristicType.BIRTHDAY,
      text: "Birthday",
      type: "text",
      id: CustomerCharacteristicType.BIRTHDAY,
    },
  ];
const dateTriggerComparisonOptions: RedoListItem<
  | TimeframeConditionType.TODAY
  | TimeframeConditionType.AFTER_NOW_RELATIVE_EXACT
  | TimeframeConditionType.BEFORE_NOW_RELATIVE_EXACT
>[] = [
  {
    value: TimeframeConditionType.TODAY,
    text: "Today",
    type: "text",
    id: TimeframeConditionType.TODAY,
  },
  {
    value: TimeframeConditionType.AFTER_NOW_RELATIVE_EXACT,
    text: "From now",
    type: "text",
    id: TimeframeConditionType.AFTER_NOW_RELATIVE_EXACT,
  },
  {
    value: TimeframeConditionType.BEFORE_NOW_RELATIVE_EXACT,
    text: "Ago",
    type: "text",
    id: TimeframeConditionType.BEFORE_NOW_RELATIVE_EXACT,
  },
];

export const DateFieldSelector = memo(function DateFieldSelector({
  value,
  valueChange,
}: {
  value: PartialWithRequiredType<MarketingDateTriggerStep>;
  valueChange: (value: PartialWithRequiredType<TriggerStep>) => void;
}) {
  const setTriggerSpecificFields = useCallback(
    (
      triggerSpecificFields: PartialWithRequiredType<MarketingDateTriggerStep>["triggerSpecificFields"],
    ) => {
      const newTimeframeType =
        triggerSpecificFields?.comparison?.type ??
        value.triggerSpecificFields?.comparison?.type;
      const newDimension =
        triggerSpecificFields?.dimension ??
        value.triggerSpecificFields?.dimension;
      const newTimeframeValue =
        triggerSpecificFields?.comparison?.options?.value ??
        value.triggerSpecificFields?.comparison?.options?.value ??
        0;

      if (
        newTimeframeType === TimeframeConditionType.TODAY ||
        !newTimeframeType
      ) {
        valueChange({
          ...value,
          triggerSpecificFields: {
            dimension: newDimension,
            comparison: { type: TimeframeConditionType.TODAY },
          },
        });
      } else if (isRelativeTimeframeType(newTimeframeType)) {
        valueChange({
          ...value,
          triggerSpecificFields: {
            dimension: newDimension,
            comparison: {
              type: newTimeframeType,
              options: { value: newTimeframeValue, units: TimeframeUnit.DAY },
            },
          },
        });
      }
    },
    [value, valueChange],
  );

  const timeframeTypeSelector = useMemo(
    () => (
      <RedoSingleSelectDropdownInput<
        | TimeframeConditionType.TODAY
        | TimeframeConditionType.AFTER_NOW_RELATIVE_EXACT
        | TimeframeConditionType.BEFORE_NOW_RELATIVE_EXACT
      >
        fitToAnchor={false}
        options={dateTriggerComparisonOptions}
        optionSelected={({ item }) => {
          let comparison: MarketingDateTriggerStep["triggerSpecificFields"]["comparison"];

          if (item.value === TimeframeConditionType.TODAY) {
            comparison = { type: item.value, options: null };
          } else if (isRelativeTimeframeType(item.value)) {
            comparison = {
              type: item.value,
              options: {
                value: 0,
                units: TimeframeUnit.DAY,
                ...value.triggerSpecificFields?.comparison?.options,
              },
            };
          } else {
            assertNever(item.value);
          }
          setTriggerSpecificFields({
            ...value.triggerSpecificFields,
            comparison,
          });
        }}
        selectedItem={dateTriggerComparisonOptions.find(
          (option) =>
            option.value === value.triggerSpecificFields?.comparison?.type,
        )}
        size="xs"
      />
    ),
    [value, setTriggerSpecificFields],
  );

  return (
    <Flex align="center" wrap="wrap">
      <TriggerText>Customer's</TriggerText>
      <RedoSingleSelectDropdownInput<CustomerCharacteristicType>
        fitToAnchor={false}
        options={dateTriggerDimensionOptions}
        optionSelected={({ item }) => {
          setTriggerSpecificFields({ dimension: item.value });
        }}
        selectedItem={dateTriggerDimensionOptions.find(
          (option) => option.value === value.triggerSpecificFields?.dimension,
        )}
        size="xs"
      />
      <TriggerText>is</TriggerText>
      {value.triggerSpecificFields?.comparison?.type ===
        TimeframeConditionType.TODAY && timeframeTypeSelector}
      {isRelativeTimeframeType(
        value.triggerSpecificFields?.comparison?.type,
      ) && (
        <>
          <RedoIncrementDecrement
            fullWidth={false}
            setValue={(numberValue) => {
              if (
                !isRelativeTimeframeType(
                  value.triggerSpecificFields?.comparison?.type,
                )
              ) {
                return;
              }
              setTriggerSpecificFields({
                comparison: {
                  options: { value: numberValue, units: TimeframeUnit.DAY },
                },
              });
            }}
            size="small"
            value={value.triggerSpecificFields?.comparison?.options?.value}
          />
          <TriggerText>day(s)</TriggerText>
          {timeframeTypeSelector}
        </>
      )}
    </Flex>
  );
});

const TriggerText = memo(function TriggerText({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <Text fontSize="xs" textColor="tertiary">
      {children}
    </Text>
  );
});

function isRelativeTimeframeType(
  type: TimeframeConditionType | undefined,
): type is
  | TimeframeConditionType.AFTER_NOW_RELATIVE_EXACT
  | TimeframeConditionType.BEFORE_NOW_RELATIVE_EXACT {
  return (
    type === TimeframeConditionType.AFTER_NOW_RELATIVE_EXACT ||
    type === TimeframeConditionType.BEFORE_NOW_RELATIVE_EXACT
  );
}
